import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-300 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '游戏攻略', path: '/article/guide-intro' },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    url: `https://ngnangong.com/article/${article.slug}`,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.path ? `https://ngnangong.com${item.path}` : undefined,
    })),
  };

  const webpageSchema = {
    name: article.title,
    description: article.excerpt,
    url: `https://ngnangong.com/article/${article.slug}`,
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-secondary-200/60">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
              </div>
              <h1 className="font-heading text-xl md:text-2xl text-foreground-900 mb-2">{article.title}</h1>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <article className="prose-custom">
            <div
              className="article-content text-sm text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          <div className="mt-8 pt-6 border-t border-secondary-200/60">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12">
              <h3 className="font-heading text-lg text-foreground-900 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors duration-200 cursor-pointer"
                  >
                    <h4 className="font-heading text-sm text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-400 line-clamp-2">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            <Link
              to="/article/guide-intro"
              className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回文章列表
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}